import React, { useCallback, useRef, useState } from 'react';
import CcmNarrativeModal from 'components/templates/ccm/patientDetails/components/narrativeTemplates/modal';
import { isCcmModule, isRpmModule } from 'infrastructure/functions';
import DeviceTroubleshootModals from 'components/organisms/device-troubleshoot-templates';
import { deleteUnnecessaryEmptyLines } from 'components/organisms/device-troubleshoot-templates/helpers';
import { ModuleType } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import { usePatientNextAppointment } from 'infrastructure/hooks/patient/use-patient-next-appointment';
import { debug } from 'utils/logger';

import ClinicalNotesModalForm from '../admin-panel/forms/clinical-notes';
import CcmInteractionStatusModal from '../ccmInteractionStatus/modal';
import { useLogic } from './logic';

import type { NextAppointment } from 'infrastructure/classes/next-appointment';

interface IProps {
  patientGuid: string;
  module: ModuleType;
  open: boolean;
  onClose: () => void;
}

const ClinicalNotesLoggedModal: React.FC<IProps> = (props) => {
  const { patientGuid, module, open, onClose } = props;
  const { t } = useTranslation();

  const { loading, updatePatientNextAppointment } = usePatientNextAppointment({
    patientGuid,
  });

  const pendingAppointmentDetailsRef = useRef<NextAppointment | null>(null);

  const handleAppointmentChange = useCallback(
    (details: NextAppointment | null) => {
      pendingAppointmentDetailsRef.current = details;
    },
    [],
  );

  const updateNextAppointment = useCallback(
    async (details: NextAppointment | null) => {
      const scheduledWithData = details?.getScheduledWithData();
      await updatePatientNextAppointment({
        patientGuid,
        appointmentDetails: details?.appointment
          ? {
              guid: details.appointment.guid,
              ...scheduledWithData,
              appointmentMethod: details.appointment.appointmentMethod,
              scheduledDate: details.prepareDate(
                details.appointment.scheduledDate,
                details.appointment.scheduledTime,
              ),
            }
          : undefined,
      });
      pendingAppointmentDetailsRef.current = null;
    },
    [patientGuid, updatePatientNextAppointment],
  );

  const getPendingAppointmentDetails = useCallback(
    () => pendingAppointmentDetailsRef.current,
    [],
  );

  const {
    defaultValue,
    MonthlyInteractionAlert,
    Alert,
    addLoading,
    narrativeModalOpen,
    isNextAppointmentModalOpen,
    onSubmit,
    onCancelForm,
    toggleOpenNarrativeModal,
    setDefaultValue,
    setIsNextAppointmentModalOpen,
    setIsTemplateBased,
    saveCcmInteractionUpdateFunction,
  } = useLogic({
    patientGuid,
    module,
    onClose,
    updateNextAppointment,
    getPendingAppointmentDetails,
  });
  const [openAppendixCModal, setOpenAppendixCModal] = useState(false);
  const [openIntroCallModal, setOpenIntroCallModal] = useState(false);
  const [openNormalReadingsModal, setOpenNormalReadingsModal] = useState(false);
  const [openDeviceTroubleshootingModal, setOpenDeviceTroubleshootingModal] =
    useState(false);
  const [openNoReadingsModal, setOpenNoReadingsModal] = useState(false);
  const [openMonthlyInteractionCall, setOpenMonthlyInteractionCall] =
    useState(false);
  const [openWrapUpModal, setOpenWrapUpModal] = useState(false);

  const btnLabel =
    module === ModuleType.RPM ? t('labels.addRpmNote') : t('labels.addCcmNote');

  const toggleAppendixCModal = () => setOpenAppendixCModal(!openAppendixCModal);
  const showDeviceTroubleshootingModal =
    openAppendixCModal ||
    openIntroCallModal ||
    openNormalReadingsModal ||
    openDeviceTroubleshootingModal ||
    openNoReadingsModal ||
    openMonthlyInteractionCall ||
    openWrapUpModal;

  const clearDeviceTroubleshootingModals = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(`device-troubleshoot_`)) {
        localStorage.removeItem(key);
      }
    });
  };

  return (
    <>
      <ClinicalNotesModalForm
        title={btnLabel}
        loading={addLoading || loading}
        onCancelForm={() => {
          onCancelForm();
          clearDeviceTroubleshootingModals();
          pendingAppointmentDetailsRef.current = null;
        }}
        onSubmit={async (values) => {
          try {
            await onSubmit(values);
            pendingAppointmentDetailsRef.current = null;
          } catch (err) {
            debug(err);
          } finally {
            clearDeviceTroubleshootingModals();
          }
        }}
        onUseTemplate={
          isCcmModule(module) ? toggleOpenNarrativeModal : undefined
        }
        onUseRpmSpecialistTemplate={
          isRpmModule(module) ? toggleAppendixCModal : undefined
        }
        defaultValues={
          defaultValue ? { note: defaultValue, module } : undefined
        }
        open={open}
      />
      {narrativeModalOpen && (
        <CcmNarrativeModal
          isOpen={narrativeModalOpen}
          closeModal={toggleOpenNarrativeModal}
          onSubmit={setDefaultValue}
          patientGuid={patientGuid}
        />
      )}
      {showDeviceTroubleshootingModal && (
        <DeviceTroubleshootModals
          patientGuid={patientGuid}
          openMonthlyInteractionCall={openMonthlyInteractionCall}
          toggleOpenMonthlyInteractionCall={setOpenMonthlyInteractionCall}
          openAppendixCModal={openAppendixCModal}
          toggleOpenAppendixCModal={setOpenAppendixCModal}
          openIntroCallModal={openIntroCallModal}
          toggleOpenIntroCallModal={setOpenIntroCallModal}
          openNormalReadingsModal={openNormalReadingsModal}
          toggleOpenNormalReadingsModal={setOpenNormalReadingsModal}
          openDeviceTroubleshootingModal={openDeviceTroubleshootingModal}
          toggleOpenDeviceTroubleshootingModal={
            setOpenDeviceTroubleshootingModal
          }
          openNoReadingsModal={openNoReadingsModal}
          toggleOpenNoReadingsModal={setOpenNoReadingsModal}
          openWrapUpModal={openWrapUpModal}
          toggleOpenWrapUpModal={setOpenWrapUpModal}
          onAppointmentChange={handleAppointmentChange}
          onSaveForm={(data: string) => {
            if (defaultValue) {
              if (openAppendixCModal) {
                setDefaultValue(data);
              } else {
                setDefaultValue(
                  deleteUnnecessaryEmptyLines(defaultValue + data),
                );
              }
            }
            if (!defaultValue) {
              setDefaultValue(data);
            }
            setIsTemplateBased(true);
          }}
        />
      )}
      {!!isNextAppointmentModalOpen && (
        <CcmInteractionStatusModal
          isOpen
          toggle={setIsNextAppointmentModalOpen}
          patientGuid={patientGuid}
          onLaterSend={saveCcmInteractionUpdateFunction}
        />
      )}
      {MonthlyInteractionAlert}
      {Alert}
    </>
  );
};

export default ClinicalNotesLoggedModal;
